import * as React from "react";
import { PreJobCheckListV2, VehicleType } from "../../../generated/nest-graphql";
import { HistoryCard } from "../HistoryCard";
import { VehicleSpecLabel, shownSpecRows, vehicleSpecRows } from "./vehicleSpecRows.utils";
import { RetryButton } from "../RetryButton";
import { NotesRow } from "./NotesRow";
import {
  BrakeDiscIcon,
  CarBatteryIcon,
  CarSideIcon,
  DocumentIcon,
  IconComponent,
  OilCanIcon,
  ParkingIcon,
  RulerIcon,
  SpeedometerIcon,
  Text,
  TextVariant,
  WrenchIcon,
} from "../../ui";

const SPEC_ICONS: Record<VehicleSpecLabel, IconComponent> = {
  [VehicleSpecLabel.MILEAGE]: SpeedometerIcon,
  [VehicleSpecLabel.TRIM]: CarSideIcon,
  [VehicleSpecLabel.REAR_DRUM_BRAKES]: BrakeDiscIcon,
  [VehicleSpecLabel.PARKING_BRAKE]: ParkingIcon,
  [VehicleSpecLabel.FRONT_ROTOR_DISCARD]: RulerIcon,
  [VehicleSpecLabel.REAR_ROTOR_DISCARD]: RulerIcon,
  [VehicleSpecLabel.WHEEL_TORQUE]: WrenchIcon,
  [VehicleSpecLabel.OIL_CAPACITY]: OilCanIcon,
  [VehicleSpecLabel.BATTERY_CCA]: CarBatteryIcon,
  [VehicleSpecLabel.NOTES]: DocumentIcon,
};

/** The vehicle record's specs, laid out like the inspection card. Only specs with a value show, and always Notes. */
export const VehicleSpecs: React.FC<{
  vehicle?: VehicleType | null;
  /** The latest inspection, which can say the rear axle has drums when the record does not. */
  inspection?: PreJobCheckListV2 | null;
  /** Where the record's mileage came from, e.g. "Pre-job checklist, 7 Jul 2026". */
  mileageSource?: string | null;
  /** Opens the notes editor; without it the notes pencil is greyed out. */
  onEditNotes?: () => void;
  /** Why the record's specs cannot be shown (it failed to load, or is gone), instead of the card. */
  unavailableReason?: string | null;
  onRetry?: () => Promise<unknown>;
}> = ({ vehicle, inspection, mileageSource, onEditNotes, unavailableReason, onRetry }) => {
  const rows = shownSpecRows(vehicleSpecRows(vehicle, inspection, mileageSource));
  return unavailableReason ? (
    <div className="flex flex-col items-center gap-3 p-6">
      <Text variant={TextVariant.BODY2} className="text-gray-400 text-center">
        {unavailableReason}
      </Text>
      {onRetry && <RetryButton onRetry={onRetry} />}
    </div>
  ) : (
    <div className="p-4">
      <HistoryCard title="Vehicle Information">
        <div className="px-3 py-1">
          {/* Notes always shows, so the card is never blank; this says the rest is simply not recorded. */}
          {rows.length === 1 && (
            <Text variant={TextVariant.BODY2} className="text-gray-400 py-2">
              No specs recorded for this vehicle.
            </Text>
          )}
          {rows.map(({ label, value, detail }) => {
            if (label === VehicleSpecLabel.NOTES) {
              return <NotesRow key={label} label={label} Icon={SPEC_ICONS[label]} notes={value} onEdit={onEditNotes} />;
            }
            const Icon = SPEC_ICONS[label];
            return (
              <div
                key={label}
                className="flex items-center justify-between gap-3 py-2 border-b border-[#44576e] last:border-b-0"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Icon aria-hidden data-testid="spec-icon" style={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }} />
                  <Text variant={TextVariant.BODY2} className="text-gray-400">
                    {label}
                  </Text>
                </span>
                <span className="flex flex-col items-end min-w-0">
                  <Text variant={TextVariant.BODY2} className="text-right" style={{ fontWeight: 600 }}>
                    {value}
                  </Text>
                  {detail && (
                    <Text variant={TextVariant.CAPTION} className="text-gray-400 text-right">
                      {detail}
                    </Text>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </HistoryCard>
    </div>
  );
};
